import type { Theme } from "@mui/material";
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import z from "zod";

import { DIALOG_WIDTH } from "../../constants";
import { CloseDialogHandler } from "../../contexts/DialogContext";
import { Schemas } from "../../schemas";
import { useUpdateIntegration } from "../../hooks/mutation/update/useUpdateIntegration";
import { useIntegration } from "../../hooks/query/useIntegration";
import { AlertSchemaPicker } from "../AlertSchemaPicker";
import StandardDialogActions from "./StandardDialogActions";
import StandardDialogTitle from "./StandardDialogTitle";

const schema = Schemas.integration
  .omit({ id: true, metadata: true, url: true, auth: true })
  .extend({ system: z.boolean().default(false) });

type FormValues = z.input<typeof schema>;

const EditIntegrationDialog: React.FC<{
  onDialogClosed: CloseDialogHandler;
  integrationId: string;
}> = ({ onDialogClosed, integrationId }) => {
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const integrationQuery = useIntegration(integrationId);
  const { mutateAsync: updateIntegration, isPending: isUpdating } = useUpdateIntegration();
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const isSystemIntegration = integrationQuery.data?.system === true;

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      description: "",
      alertSchemaId: "",
      system: false,
    },
    mode: "onBlur",
  });

  React.useEffect(() => {
    const integration = integrationQuery.data;
    if (!integration) {return;}

    reset({
      name: integration.name,
      description: integration.description ?? "",
      alertSchemaId: integration.alertSchemaId,
      system: integration.system ?? false,
    });
  }, [integrationQuery.data, reset]);

  const onSubmit = async (data: FormValues) => {
    try {
      if (isSystemIntegration) {
        setSubmitError("System integrations cannot be edited.");
        return;
      }
      setSubmitError(null);
      await updateIntegration({ integrationId, integration: data });
      onDialogClosed({ message: "Integration updated!", severity: "success" });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong.";
      setSubmitError(msg);
    }
  };

  const onInvalid = (errs: Record<string, unknown>) => {
    console.error(errs);
    setSubmitError("Error during submit");
  };

  const onClose = () => {
    if (isUpdating) {return;}
    setSubmitError(null);
    reset({ name: "", description: "", alertSchemaId: "", system: false });
    onDialogClosed();
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullWidth
      fullScreen={isSmallScreen}
      PaperProps={{ sx: { maxWidth: isSmallScreen ? undefined : DIALOG_WIDTH.lg } }}
    >
      <StandardDialogTitle title={"Edit Integration"} isSmallScreen={isSmallScreen} onClose={onClose} />

      <Box component="form" id="edit-integration-form" onSubmit={handleSubmit(onSubmit, onInvalid)}>
        <DialogContent sx={{ overflowX: "hidden" }}>
          <Box sx={{ width: "100%" }}>
            <Stack sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Create a integration and optionally declare its dependencies.
              </Typography>
            </Stack>

            <Divider sx={{ mb: 1.5 }} />

            <Stack spacing={2.25} sx={{ width: "100%" }}>
              {isSystemIntegration && (
                <FormHelperText sx={{ fontSize: 13, m: 0 }}>
                  System integrations are read-only.
                </FormHelperText>
              )}

              {submitError && (
                <FormHelperText error sx={{ fontSize: 13, m: 0 }}>
                  {submitError}
                </FormHelperText>
              )}

              <FormControl>
                <FormLabel>Name</FormLabel>
                <TextField
                  size="small"
                  placeholder="e.g. Sentry organization integration"
                  disabled={isSystemIntegration}
                  error={!!errors.name}
                  {...register("name", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.name}>{errors.name?.message ?? " "}</FormHelperText>
              </FormControl>

              <FormControl error={!!errors.alertSchemaId}>
                <FormLabel>Alert schema</FormLabel>

                <Controller
                  name="alertSchemaId"
                  control={control}
                  render={({ field }) => (
                    <AlertSchemaPicker
                      disabled={isSystemIntegration}
                      value={field.value ?? ""}
                      onChange={(e) => {
                        field.onChange(e);
                        if (submitError) {
                          setSubmitError(null);
                        }
                      }}
                    />
                  )}
                />

                <FormHelperText>{errors.alertSchemaId?.message ?? " "}</FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <TextField
                  size="small"
                  multiline
                  minRows={2}
                  placeholder="Short description of the system"
                  disabled={isSystemIntegration}
                  error={!!errors.description}
                  {...register("description", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.description}>
                  {errors.description?.message ?? " "}
                </FormHelperText>
              </FormControl>
            </Stack>
          </Box>
        </DialogContent>

        <StandardDialogActions
          primaryText={"Save"}
          primaryIcon={<></>}
          onCancel={onClose}
          loading={isUpdating}
          disabledReason={isSystemIntegration ? "System integrations are read-only." : undefined}
          disabled={!integrationQuery.data || isUpdating || isSystemIntegration}
        />
      </Box>
    </Dialog>
  );
};

export default EditIntegrationDialog;
